import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import Pagination from '@epoch-components/tables/Pagination';
import LoadingSpinner from 'components/common/loaders/LoadingSpinner';
import { Question } from 'types/question.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { getPaginatedResponses } from '@event-editor/api/feedback/getPaginatedResponses';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import useFeatureFlag from 'hooks/useFeatureFlag';
import { AI_INTEGRATION_V1 } from 'constants/featureFlags';
import { GenerateFeedbackInsightModal } from 'components/common/AIModals/feedback/GenerateFeedbackInsightModal';
import { AIFeedbackSummary } from 'components/common/AIModals/feedback/AIFeedbackSummary/AIFeedbackSummary';
import { AITriggerModalButton } from 'components/common/AIModals/AITriggerModalButton';

const RESPONSES_PER_PAGE = 10;

interface PaginatedQuestionResponsesProps {
  question: Question;
  responsesTotal: number;
}

export const PaginatedQuestionResponses: React.FC<PaginatedQuestionResponsesProps> = ({
  question,
  responsesTotal
}) => {
  const { showToast } = useToaster();
  const [currentPage, setCurrentPage] = useState(1);
  const { id: eventId } = useEditableEvent();
  const isAIIntegrationEnabled = useFeatureFlag(AI_INTEGRATION_V1);
  const [feedbackIdAIInsights, setFeedbackIdAIInsight] = useState<number | undefined>();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: eventEditorQueryKeys.eventPaginatedQuestionnaireResponses(
      Number(question.id),
      currentPage
    ),
    queryFn: async () => {
      const result = await getPaginatedResponses({
        questionId: Number(question.id),
        page: currentPage - 1 // API expects 0-based index
      });
      return result;
    },
    enabled: typeof question.id === 'number' && !Number.isNaN(question.id),
    placeholderData: (previousData) => previousData
  });

  if (isLoading && !data) {
    // Only show loading on first load
    return <LoadingSpinner />;
  }

  if (error) {
    console.error('Failed to fetch paginated responses:', error);
    showToast('Failed to load question responses. Please try again.', {
      variant: ToasterVariant.ERROR
    });
  }

  const responses = data?.paginatedResponses?.responses || [];
  const totalPages = data?.paginatedResponses?.pages || 0;
  const total = data?.paginatedResponses?.total || 0;
  const feedbackSummary = data?.paginatedResponses?.feedbackSummary;

  return (
    <div>
      {feedbackIdAIInsights && (
        <GenerateFeedbackInsightModal
          isOpen={!!feedbackIdAIInsights}
          questionId={feedbackIdAIInsights.toString()}
          eventId={eventId?.toString() || ''}
          onRequestClose={() => {
            setFeedbackIdAIInsight(undefined);
          }}
          questionTitle={question.label}
          refetchList={refetch}
          showToastError={() => {
            setFeedbackIdAIInsight(undefined);
            showToast('Summary could not be generated. Try again later.', {
              variant: ToasterVariant.ERROR
            });
          }}
        />
      )}
      <div className="tw-mb-3">
        <div className="tw-flex tw-items-center tw-justify-between">
          <div>
            <p className="tw-mb-1.5 tw-text-sm tw-font-medium tw-leading-4 tw-text-black">
              {question.label}
            </p>
            <p className="tw-m-0 tw-text-xs tw-leading-4 tw-text-primary-grey">
              {total} of {responsesTotal} responses
            </p>
          </div>

          {isAIIntegrationEnabled && !feedbackSummary && (
            <AITriggerModalButton
              handleClick={() => {
                setFeedbackIdAIInsight(Number(question.id));
              }}
              label="Summarize"
            />
          )}
        </div>
      </div>

      {isAIIntegrationEnabled && feedbackSummary && (
        <div className="tw-mb-3">
          <AIFeedbackSummary
            feedbackSummary={feedbackSummary}
            setOpenGenerateFeedbackModal={() => setFeedbackIdAIInsight(Number(question.id))}
            refetchList={refetch}
          />
        </div>
      )}

      {total === 0 ? (
        <div className="tw-rounded-lg tw-border tw-border-solid tw-border-border-grey tw-bg-background tw-p-6">
          <p className="tw-mb-0 tw-text-sm tw-leading-5 tw-text-medium-grey">
            No responses for this question.
          </p>
        </div>
      ) : (
        <>
          <ul className="tw-my-3 tw-list-none tw-p-0">
            {responses.map((response, index) => (
              <li
                key={index}
                className="tw-mb-2 tw-rounded-lg tw-bg-light-grey tw-p-3 tw-leading-5"
              >
                {typeof response === 'string' ? response : response.response || response.toString()}
              </li>
            ))}
          </ul>

          {totalPages > 1 && (
            <Pagination
              perPage={RESPONSES_PER_PAGE}
              currPage={currentPage}
              totalCount={total}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
};
